import { z } from 'zod';
/**
 * Nutrição, etapa N1 (estudo de 23/10): gasto energético, metas de macronutrientes e a base
 * de alimentos. As contas moram aqui para a mesa, o bolso e o servidor darem o mesmo número.
 * A fórmula, o fator e as metas são escolha do nutricionista; o app só faz a conta e avisa.
 */
const isoDateTime = z.iso.datetime();
// ── Gasto energético ─────────────────────────────────────────────────────────
export const ENERGY_METHODS = ['mifflin', 'harris', 'cunningham'];
export const energyMethodSchema = z.enum(ENERGY_METHODS);
export const ENERGY_METHOD_INFO = {
    mifflin: {
        label: 'Mifflin-St Jeor',
        hint: 'Peso, altura, idade e sexo. A mais usada para adultos.',
        needsLeanMass: false,
    },
    harris: {
        label: 'Harris-Benedict revisada',
        hint: 'A revisão de 1984 (Roza e Shizgal).',
        needsLeanMass: false,
    },
    cunningham: {
        label: 'Cunningham',
        hint: 'Pela massa magra da bioimpedância. Boa para quem treina.',
        needsLeanMass: true,
    },
};
export const ACTIVITY_LEVELS = [
    'sedentario',
    'leve',
    'moderado',
    'intenso',
    'muito_intenso',
];
export const activityLevelSchema = z.enum(ACTIVITY_LEVELS);
export const ACTIVITY_INFO = {
    sedentario: { label: 'Sedentário', factor: 1.2, hint: 'Trabalho sentado, sem exercício' },
    leve: { label: 'Leve', factor: 1.375, hint: 'Anda bastante no dia a dia' },
    moderado: { label: 'Moderado', factor: 1.55, hint: 'Trabalho em pé ou de movimento' },
    intenso: { label: 'Intenso', factor: 1.725, hint: 'Trabalho físico pesado' },
    muito_intenso: {
        label: 'Muito intenso',
        factor: 1.9,
        hint: 'Atleta ou trabalho braçal o dia todo',
    },
};
/** Exercício da semana, somado pelo MET (o gasto acima do repouso, para não contar duas vezes). */
export const nutritionExerciseSchema = z.object({
    label: z.string().trim().min(1).max(40),
    met: z.number().min(1.5).max(20),
    minutesWeek: z.number().int().min(10).max(2400),
});
/** Atividades comuns com o MET do Compêndio de Atividades Físicas (2011). */
export const COMMON_EXERCISES = [
    { label: 'Caminhada', met: 3.5 },
    { label: 'Caminhada rápida', met: 5 },
    { label: 'Corrida leve', met: 8 },
    { label: 'Musculação', met: 5 },
    { label: 'Bicicleta', met: 7 },
    { label: 'Natação', met: 6 },
    { label: 'Pilates', met: 3 },
    { label: 'Dança', met: 5 },
    { label: 'Futebol', met: 7 },
    { label: 'Luta', met: 10 },
];
export const nutritionTargetsSchema = z.object({
    method: energyMethodSchema,
    activity: activityLevelSchema,
    exercises: z.array(nutritionExerciseSchema).max(6).default([]),
    /** Ajuste sobre o gasto total: negativo para perder, positivo para ganhar. */
    adjustKcal: z.number().int().min(-1500).max(1500).default(0),
    proteinGPerKg: z.number().min(0.5).max(3.5),
    fatGPerKg: z.number().min(0.3).max(2.5),
    /** Quando o nutricionista quer outro número que não o da última avaliação. */
    weightKg: z.number().min(10).max(400).optional(),
    heightCm: z.number().min(80).max(250).optional(),
    leanMassKg: z.number().min(10).max(200).optional(),
    note: z.string().trim().max(300).optional(),
});
export const DEFAULT_NUTRITION_TARGETS = {
    method: 'mifflin',
    activity: 'leve',
    exercises: [],
    adjustKcal: 0,
    proteinGPerKg: 1.6,
    fatGPerKg: 0.8,
};
/** O que o app sabe da pessoa para a conta: a ficha e a última avaliação. */
export const nutritionInputsSchema = z.object({
    sex: z.enum(['f', 'm']).nullable(),
    ageYears: z.number().int().nullable(),
    weightKg: z.number().nullable(),
    heightCm: z.number().nullable(),
    leanMassKg: z.number().nullable(),
    measuredAt: isoDateTime.nullable(),
    source: z.enum(['bioimpedancia', 'antropometria']).nullable(),
});
const macroSchema = z.object({ grams: z.number(), kcal: z.number(), percent: z.number() });
export const nutritionSummarySchema = z.object({
    bmr: z.number(),
    activityFactor: z.number(),
    exerciseKcal: z.number(),
    tee: z.number(),
    targetKcal: z.number(),
    protein: macroSchema,
    fat: macroSchema,
    carb: macroSchema,
    /** Avisos para o nutricionista, nunca uma trava. */
    warnings: z.array(z.string()),
});
export const patientNutritionSchema = z.object({
    inputs: nutritionInputsSchema,
    targets: nutritionTargetsSchema.nullable(),
    /** A conta com as metas salvas (ou as de partida, quando ainda não há). */
    summary: nutritionSummarySchema.nullable(),
    /** O que falta para fazer a conta ("altura", "massa magra"). */
    missing: z.array(z.string()),
    updatedAt: isoDateTime.nullable(),
    updatedBy: z.string().nullable(),
});
const round = (value, places = 0) => {
    const step = 10 ** places;
    return Math.round(value * step) / step;
};
/** Taxa metabólica basal, em kcal por dia, pela fórmula escolhida. */
export function basalMetabolicRate(method, input) {
    const { sex, weightKg: w, heightCm: h, ageYears: a } = input;
    switch (method) {
        case 'mifflin':
            return 10 * w + 6.25 * h - 5 * a + (sex === 'm' ? 5 : -161);
        case 'harris':
            return sex === 'm'
                ? 88.362 + 13.397 * w + 4.799 * h - 5.677 * a
                : 447.593 + 9.247 * w + 3.098 * h - 4.33 * a;
        case 'cunningham':
            return input.leanMassKg ? 500 + 22 * input.leanMassKg : null;
    }
}
/** Kcal por dia dos exercícios da semana, só o que passa do repouso: (MET − 1) × kg × horas ÷ 7. */
export function exerciseKcalPerDay(exercises, weightKg) {
    return exercises.reduce((sum, item) => sum + ((item.met - 1) * weightKg * (item.minutesWeek / 60)) / 7, 0);
}
/** O que falta na ficha ou na avaliação para a conta com estas metas. */
export function nutritionMissing(inputs, targets) {
    const missing = [];
    const weight = targets.weightKg ?? inputs.weightKg;
    const height = targets.heightCm ?? inputs.heightCm;
    const lean = targets.leanMassKg ?? inputs.leanMassKg;
    if (!weight)
        missing.push('peso');
    if (targets.method === 'cunningham') {
        if (!lean)
            missing.push('massa magra');
    }
    else {
        if (!height)
            missing.push('altura');
        if (inputs.ageYears === null)
            missing.push('data de nascimento');
        if (!inputs.sex)
            missing.push('sexo');
    }
    return missing;
}
/** A conta inteira: TMB, gasto total, meta e os três macronutrientes. */
export function computeNutrition(inputs, targets) {
    if (nutritionMissing(inputs, targets).length)
        return null;
    const weightKg = (targets.weightKg ?? inputs.weightKg);
    const bmr = basalMetabolicRate(targets.method, {
        sex: inputs.sex ?? 'f',
        weightKg,
        heightCm: targets.heightCm ?? inputs.heightCm ?? 0,
        ageYears: inputs.ageYears ?? 0,
        leanMassKg: targets.leanMassKg ?? inputs.leanMassKg,
    });
    if (bmr === null)
        return null;
    const factor = ACTIVITY_INFO[targets.activity].factor;
    const exerciseKcal = exerciseKcalPerDay(targets.exercises, weightKg);
    const tee = bmr * factor + exerciseKcal;
    const targetKcal = Math.max(0, tee + targets.adjustKcal);
    const proteinG = targets.proteinGPerKg * weightKg;
    const fatG = targets.fatGPerKg * weightKg;
    const carbKcal = Math.max(0, targetKcal - proteinG * 4 - fatG * 9);
    const carbG = carbKcal / 4;
    const pct = (kcal) => (targetKcal > 0 ? round((kcal / targetKcal) * 100) : 0);
    const warnings = [];
    if (targetKcal < bmr)
        warnings.push('A meta ficou abaixo da taxa basal.');
    if (proteinG * 4 + fatG * 9 > targetKcal)
        warnings.push('Proteína e gordura já passam da meta; não sobra carboidrato.');
    else if (carbG < 130)
        warnings.push('Menos de 130 g de carboidrato por dia.');
    return {
        bmr: round(bmr),
        activityFactor: factor,
        exerciseKcal: round(exerciseKcal),
        tee: round(tee),
        targetKcal: round(targetKcal),
        protein: { grams: round(proteinG), kcal: round(proteinG * 4), percent: pct(proteinG * 4) },
        fat: { grams: round(fatG), kcal: round(fatG * 9), percent: pct(fatG * 9) },
        carb: { grams: round(carbG), kcal: round(carbKcal), percent: pct(carbKcal) },
        warnings,
    };
}
// ── Base de alimentos ────────────────────────────────────────────────────────
export const FOOD_SOURCES = ['taco', 'ibge', 'usda', 'off', 'proprio'];
export const foodSourceSchema = z.enum(FOOD_SOURCES);
/** De onde veio o número: aparece em cada alimento e no rodapé do plano. */
export const FOOD_SOURCE_INFO = {
    taco: {
        label: 'TACO',
        citation: 'NEPA/Unicamp. Tabela Brasileira de Composição de Alimentos (TACO), 4ª edição revisada e ampliada. Campinas, 2011.',
    },
    ibge: {
        label: 'IBGE',
        citation: 'IBGE. Pesquisa de Orçamentos Familiares 2008-2009: tabelas de composição nutricional dos alimentos consumidos no Brasil e tabela de medidas referidas. Rio de Janeiro, 2011.',
    },
    usda: {
        label: 'USDA',
        citation: 'U.S. Department of Agriculture, Agricultural Research Service. FoodData Central (SR Legacy, Foundation Foods, FNDDS), domínio público; nomes traduzidos pelo Sec.',
    },
    off: {
        label: 'Rótulo',
        citation: 'Open Food Facts (openfoodfacts.org), base aberta sob a licença ODbL; valores do rótulo informados pelo fabricante.',
    },
    proprio: { label: 'Da clínica', citation: 'Cadastrado pela equipe da clínica.' },
};
export const FOOD_GROUPS = [
    'cereais',
    'leguminosas',
    'verduras',
    'frutas',
    'carnes',
    'pescados',
    'ovos',
    'leites',
    'oleaginosas',
    'gorduras',
    'acucares',
    'molhos',
    'bebidas',
    'preparacoes',
    'suplementos',
    'outros',
];
export const foodGroupSchema = z.enum(FOOD_GROUPS);
export const FOOD_GROUP_LABEL = {
    cereais: 'Cereais, pães e tubérculos',
    leguminosas: 'Leguminosas',
    verduras: 'Verduras e legumes',
    frutas: 'Frutas',
    carnes: 'Carnes',
    pescados: 'Peixes e frutos do mar',
    ovos: 'Ovos',
    leites: 'Leite e derivados',
    oleaginosas: 'Castanhas e sementes',
    gorduras: 'Óleos e gorduras',
    acucares: 'Açúcares e doces',
    molhos: 'Molhos e temperos',
    bebidas: 'Bebidas',
    preparacoes: 'Preparações',
    suplementos: 'Suplementos',
    outros: 'Outros',
};
/** Medida caseira: "colher de sopa" = 15 g. */
export const foodMeasureSchema = z.object({
    label: z.string().trim().min(1).max(40),
    grams: z.number().min(0.1).max(5000),
});
const per100 = (max) => z.number().min(0).max(max);
export const saveClinicFoodSchema = z.object({
    name: z.string().trim().min(2).max(120),
    group: foodGroupSchema,
    /** Valores por 100 g (ou 100 ml). */
    kcal: per100(950),
    protein: per100(100),
    carb: per100(100),
    fat: per100(100),
    fiber: per100(100).nullable().optional(),
    sodiumMg: per100(40000).nullable().optional(),
    measures: z.array(foodMeasureSchema).max(8).default([]),
});
export const clinicFoodSchema = saveClinicFoodSchema.extend({
    id: z.uuid(),
    /** As tabelas trazem mais medidas que o cadastro da clínica (IBGE: até dezenas por alimento). */
    measures: z.array(foodMeasureSchema).max(40),
    source: foodSourceSchema,
    /** O código do alimento na tabela de origem. */
    sourceRef: z.string().nullable(),
    fiber: z.number().nullable(),
    sodiumMg: z.number().nullable(),
    /** Da própria clínica: dá para mudar e tirar. */
    mine: z.boolean(),
});
export const clinicFoodListSchema = z.object({
    foods: z.array(clinicFoodSchema),
    /** Quantos alimentos a base tem ao todo (das tabelas e da clínica). */
    total: z.number().int(),
});
/** Kcal e macronutrientes de uma quantidade em gramas. */
export function foodPortion(food, grams) {
    const k = grams / 100;
    return {
        kcal: round(food.kcal * k),
        protein: round(food.protein * k, 1),
        carb: round(food.carb * k, 1),
        fat: round(food.fat * k, 1),
    };
}
/**
 * Nomes regionais e populares do mesmo alimento. A busca troca cada palavra pelo grupo
 * inteiro: "macaxeira" acha "Aipim" e "Mandioca". Tudo sem acento, no singular.
 */
export const FOOD_SYNONYMS = [
    ['aipim', 'mandioca', 'macaxeira'],
    ['mexerica', 'tangerina', 'bergamota', 'mimosa', 'ponkan'],
    ['abobora', 'jerimum'],
    ['mandioquinha', 'batata baroa', 'batata salsa'],
    ['pao frances', 'pao de sal', 'cacetinho', 'pao careca', 'pao carioquinha'],
    ['feijao de corda', 'feijao verde', 'feijao fradinho', 'feijao macassar'],
    ['aipo', 'salsao'],
    ['vagem', 'feijao vagem'],
    ['biscoito', 'bolacha'],
    ['frango', 'galinha'],
    ['carne seca', 'charque', 'jaba', 'carne de sol'],
    ['aguardente', 'cachaca', 'pinga'],
    ['tapioca', 'beiju'],
    ['queijo minas', 'queijo branco', 'queijo frescal'],
    ['iogurte', 'yogurt', 'yogurte'],
    ['refrigerante', 'refri'],
    ['salgadinho', 'chips'],
    ['cuscuz', 'cuscuz de milho'],
    ['milho verde', 'espiga de milho'],
    ['maracuja', 'passiflora'],
    ['jabuticaba', 'jaboticaba'],
    ['amendoim', 'mendubi'],
    ['abacaxi', 'anana'],
    ['beterraba', 'beterava'],
    ['carne moida', 'patinho moido', 'acem moido'],
    ['peito de frango', 'file de frango'],
    ['contrafile', 'contra file', 'bife ancho'],
    ['picanha', 'tampa de alcatra'],
    ['coxao mole', 'chan de dentro'],
    ['coxao duro', 'chan de fora'],
    ['whey', 'proteina do soro do leite'],
    ['achocolatado', 'chocolate em po'],
    ['adocante', 'edulcorante'],
    ['requeijao', 'catupiry'],
    ['mortadela', 'mortandela'],
    ['salsicha', 'hot dog', 'vina'],
    ['suco', 'refresco'],
    ['cafe com leite', 'pingado'],
];
/** Plural simples para o singular: "ovos" → "ovo", "pães" → "pao", "colheres" → "colher". */
export function foodStem(token) {
    if (token.length <= 3 || /^\d/.test(token))
        return token;
    if (/(oes|aes|aos)$/.test(token))
        return `${token.slice(0, -3)}ao`;
    if (token.endsWith('ns'))
        return `${token.slice(0, -2)}m`;
    if (/(res|zes|ses)$/.test(token))
        return token.slice(0, -2);
    if (token.endsWith('is') && token.length > 4)
        return `${token.slice(0, -2)}l`;
    if (token.endsWith('s'))
        return token.slice(0, -1);
    return token;
}
/** As palavras de busca de um nome, sem acento e no singular. */
export function foodTokens(text) {
    return foodSearchKey(text)
        .split(' ')
        .filter((token) => token.length > 0)
        .map(foodStem);
}
const SYNONYM_INDEX = (() => {
    const index = new Map();
    for (const group of FOOD_SYNONYMS) {
        const forms = group.map((term) => foodTokens(term));
        for (const form of forms) {
            const key = form.join(' ');
            index.set(key, forms.filter((other) => other.join(' ') !== key));
        }
    }
    return index;
})();
/**
 * O que a busca procura: as palavras digitadas e, para cada nome regional reconhecido, os
 * outros nomes do mesmo alimento. Cada alternativa é uma lista de palavras que precisam
 * aparecer juntas.
 */
export function foodQueryAlternatives(query) {
    const tokens = foodTokens(query);
    if (tokens.length === 0)
        return [];
    const out = [tokens];
    for (let size = Math.min(3, tokens.length); size >= 1; size -= 1) {
        for (let start = 0; start + size <= tokens.length; start += 1) {
            const phrase = tokens.slice(start, start + size).join(' ');
            for (const other of SYNONYM_INDEX.get(phrase) ?? []) {
                out.push([...tokens.slice(0, start), ...other, ...tokens.slice(start + size)]);
            }
        }
    }
    return out.slice(0, 12);
}
/** Distância de edição limitada a 1 (troca, falta ou sobra de uma letra). */
function withinOneEdit(a, b) {
    if (a === b)
        return true;
    if (Math.abs(a.length - b.length) > 1)
        return false;
    let i = 0;
    let j = 0;
    let edits = 0;
    while (i < a.length && j < b.length) {
        if (a[i] === b[j]) {
            i += 1;
            j += 1;
            continue;
        }
        edits += 1;
        if (edits > 1)
            return false;
        if (a.length > b.length)
            i += 1;
        else if (b.length > a.length)
            j += 1;
        else {
            i += 1;
            j += 1;
        }
    }
    return edits + (a.length - i) + (b.length - j) <= 1;
}
/**
 * Quanto um nome casa com a busca (0 = não casa). Cada palavra da busca precisa achar uma
 * palavra do nome: igual vale mais, começo de palavra vale menos, e uma letra trocada (só
 * em palavras de 5 letras ou mais) vale menos ainda. Nome mais curto ganha no empate.
 */
export function foodMatchScore(nameTokens, query, 
/** Palavras do nome fora dos parênteses ("Arroz (polido, agulha…)" conta como uma). */
coreLength = nameTokens.length) {
    if (query.length === 0)
        return 1;
    // "de", "com", "e" não precisam casar (senão "pão de sal" acha qualquer "salgado de...").
    const words = query.filter((word) => !CONNECTORS.has(word));
    const wanted = words.length > 0 ? words : query;
    const tokens = nameTokens.filter((token) => !CONNECTORS.has(token));
    let score = 0;
    for (const [position, word] of wanted.entries()) {
        let best = 0;
        for (const [index, token] of tokens.entries()) {
            let points = 0;
            if (token === word)
                points = 10;
            else if (word.length >= 2 && token.startsWith(word))
                points = 6;
            else if (word.length >= 5 && withinOneEdit(token, word))
                points = 4;
            if (points > 0 && index === position)
                points += 2;
            if (points > best)
                best = points;
        }
        if (best === 0)
            return 0;
        score += best;
    }
    // Média por palavra: nome regional de três palavras não ganha só por ser mais longo.
    const exact = tokens.length === wanted.length && wanted.every((word, i) => tokens[i] === word);
    return score / wanted.length + (exact ? 3 : 0) + Math.max(0, 8 - coreLength) * 0.25;
}
const CONNECTORS = new Set([
    'de',
    'da',
    'do',
    'das',
    'dos',
    'com',
    'e',
    'em',
    'a',
    'o',
    'ao',
    'na',
    'no',
    'para',
]);
/** Busca sem acento e sem caixa: "pao frances" acha "Pão, francês". */
export function foodSearchKey(text) {
    return text
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
}
